(() => {
  if (window.AWSliderCoreUtils) {
    return;
  }

  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  function getScrollBehavior() {
    return reducedMotionQuery.matches ? "auto" : "smooth";
  }

  function updateButtonState(button, isDisabled) {
    if (!button) {
      return;
    }

    button.disabled = false;
    button.classList.toggle("is-disabled", isDisabled);
    button.setAttribute("aria-disabled", isDisabled ? "true" : "false");
  }

  function bindImageLoad(root, handler) {
    if (!root || typeof root.querySelectorAll !== "function") {
      return;
    }

    root.querySelectorAll("img").forEach((image) => {
      if (image.complete) {
        return;
      }

      image.addEventListener("load", handler, { once: true });
    });
  }

  function unbindImageLoad(root, handler) {
    if (!root || typeof root.querySelectorAll !== "function") {
      return;
    }

    root.querySelectorAll("img").forEach((image) => {
      image.removeEventListener("load", handler);
    });
  }

  function getMaxScroll(viewport) {
    if (!viewport) {
      return 0;
    }

    return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  }

  function getGap(list) {
    if (!list) {
      return 0;
    }

    const styles = window.getComputedStyle(list);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || "0");

    return Number.isFinite(gap) ? gap : 0;
  }

  function getFirstItem(list, items, itemSelector) {
    if (typeof itemSelector === "string" && list) {
      const selectedItem = list.querySelector(itemSelector);

      if (selectedItem) {
        return selectedItem;
      }
    }

    if (Array.isArray(items) && items[0]) {
      return items[0];
    }

    if (list && list.children && list.children[0]) {
      return list.children[0];
    }

    return null;
  }

  function getItemDistance({ list, items, itemSelector, viewport }) {
    const item = getFirstItem(list, items, itemSelector);

    if (!item) {
      return viewport ? viewport.clientWidth : 0;
    }

    return item.getBoundingClientRect().width + getGap(list);
  }

  function scrollByItems({ viewport, list, items, itemSelector, step = 1 }) {
    if (!viewport) {
      return;
    }

    const distance = getItemDistance({ list, items, itemSelector, viewport });

    if (!distance) {
      return;
    }

    viewport.scrollBy({
      left: distance * step,
      behavior: getScrollBehavior(),
    });
  }

  function updateScrollbar({ viewport, scrollbar, thumb, enabled = true, minThumbWidth = 24 }) {
    if (!viewport || !thumb) {
      return;
    }

    const maxScroll = getMaxScroll(viewport);
    const current = Math.max(0, viewport.scrollLeft);
    const hasOverflow = maxScroll > 1;

    if (!enabled || !hasOverflow || !scrollbar) {
      thumb.style.width = "100%";
      thumb.style.transform = "translateX(0px)";
      return;
    }

    const trackWidth = scrollbar.clientWidth;
    const ratio = viewport.clientWidth / viewport.scrollWidth;
    const thumbWidth = Math.max(trackWidth * ratio, minThumbWidth);
    const maxThumbX = Math.max(0, trackWidth - thumbWidth);
    const progress = Math.min(Math.max(current / maxScroll, 0), 1);
    const thumbX = maxThumbX * progress;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${thumbX}px)`;
  }

  function requestFrame(instance, key, callback) {
    if (instance[key]) {
      return;
    }

    instance[key] = window.requestAnimationFrame(() => {
      instance[key] = null;
      callback();
    });
  }

  function cancelFrame(instance, key) {
    if (!instance[key]) {
      return;
    }

    window.cancelAnimationFrame(instance[key]);
    instance[key] = null;
  }

  function observeResize(targets, callback) {
    const validTargets = Array.isArray(targets) ? targets.filter(Boolean) : [];

    if ("ResizeObserver" in window) {
      const resizeObserver = new ResizeObserver(callback);

      validTargets.forEach((target) => {
        resizeObserver.observe(target);
      });

      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener("resize", callback);

    return () => {
      window.removeEventListener("resize", callback);
    };
  }

  function selectBlockItem({ root, viewport, itemSelector, blockId, fallbackTarget }) {
    let item = null;

    if (blockId && root && typeof root.querySelector === "function") {
      item = root.querySelector(`${itemSelector}[data-block-id="${blockId}"]`);
    }

    if (!item && fallbackTarget && typeof fallbackTarget.closest === "function") {
      item = fallbackTarget.closest(itemSelector);
    }

    if (!item || !viewport) {
      return;
    }

    const itemRect = item.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();

    viewport.scrollTo({
      left: viewport.scrollLeft + itemRect.left - viewportRect.left,
      behavior: getScrollBehavior(),
    });
  }

  window.AWSliderCoreUtils = {
    bindImageLoad,
    cancelFrame,
    getGap,
    getItemDistance,
    getMaxScroll,
    getScrollBehavior,
    observeResize,
    requestFrame,
    scrollByItems,
    selectBlockItem,
    unbindImageLoad,
    updateButtonState,
    updateScrollbar,
  };
})();
